import { clientEnv } from "../env";
import type {
  AccountSession,
  AuthProviderStatus,
  AuthSessionResponse,
  EmailDeliveryStatus,
  MfaLoginChallenge,
  MfaSetup,
  MfaStatus,
  PasswordLoginResult
} from "./auth.types";

interface ApiEnvelope<T> {
  readonly data: T;
}

interface ApiErrorPayload {
  readonly error?: string;
  readonly message?: string;
}

const SESSION_STORAGE_KEY = "omnisynia.auth.session.v1";

export class AuthClientError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AuthClientError";
    this.status = status;
    this.code = code;
  }
}

export async function getOrCreateAuthSession(): Promise<AuthSessionResponse> {
  const storedSession = readStoredSession();
  if (storedSession && Date.parse(storedSession.expiresAt) > Date.now()) {
    try {
      const response = await fetch(`${clientEnv.apiBaseUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${storedSession.sessionToken}` }
      });
      if (response.ok) {
        const user = await readJson<PasswordSessionPayload["user"]>(response);
        const verified = {
          ...storedSession,
          user: {
            ...storedSession.user,
            plan: user.plan ?? storedSession.user.plan,
            email: user.email ?? storedSession.user.email,
            displayName: user.displayName ?? storedSession.user.displayName,
            emailVerified: user.emailVerified,
            role: user.role,
            mfaEnabled: user.mfaEnabled
          }
        };
        writeStoredSession(verified);
        return verified;
      }
      if (response.status === 401 || response.status === 403 || response.status === 404) clearStoredAuthSession();
      else return storedSession;
    } catch {
      return storedSession;
    }
  }

  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/guest`, {
    method: "POST"
  });
  const payload = await readJson<{
    user?: { id: string; emailVerified?: boolean };
    sessionId?: string;
    token?: string;
    expiresAt?: number;
  } | ApiErrorPayload>(response);

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(
      response.status,
      errorPayload.error ?? "AUTH_SESSION_FAILED",
      errorPayload.message ?? "No se pudo iniciar una sesión segura."
    );
  }

  const guest = payload as {
    user: { id: string; emailVerified?: boolean };
    sessionId: string;
    token: string;
    expiresAt: number;
  };
  const session: AuthSessionResponse = {
    sessionId: guest.sessionId,
    sessionToken: guest.token,
    tokenType: "Bearer",
    createdAt: new Date().toISOString(),
    expiresAt: new Date(guest.expiresAt).toISOString(),
    user: {
      id: guest.user.id,
      mode: "anonymous",
      plan: "free",
      ageGate: "unknown",
      provider: "anonymous",
      emailVerified: guest.user.emailVerified
    }
  };
  writeStoredSession(session);
  return session;
}

interface PasswordSessionPayload {
  user: {
    id: string;
    email?: string;
    displayName?: string;
    plan?: AuthSessionResponse["user"]["plan"];
    emailVerified?: boolean;
    role?: AuthSessionResponse["user"]["role"];
    mfaEnabled?: boolean;
  };
  sessionId: string;
  token: string;
  expiresAt: number;
}

export async function registerPasswordAccount(input: {
  email: string;
  password: string;
  displayName?: string;
}): Promise<AuthSessionResponse> {
  return createPasswordSession("register", input);
}

export async function loginPasswordAccount(input: {
  email: string;
  password: string;
}): Promise<PasswordLoginResult> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  const payload = await readJson<PasswordSessionPayload | MfaLoginChallenge | ApiErrorPayload>(response);
  if (!response.ok) throwAuthError(response, payload, "No se pudo iniciar sesión.");
  if (isMfaLoginChallenge(payload)) return payload;
  return storePasswordSession(payload as PasswordSessionPayload);
}

export function isMfaLoginChallenge(value: unknown): value is MfaLoginChallenge {
  return Boolean(value && typeof value === "object" && (value as { mfaRequired?: unknown }).mfaRequired === true);
}

export async function completeMfaLogin(challengeToken: string, code: string): Promise<AuthSessionResponse> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/mfa/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: challengeToken, code })
  });
  const payload = await readJson<PasswordSessionPayload | ApiErrorPayload>(response);
  if (!response.ok) throwAuthError(response, payload, "No se pudo comprobar el código.");
  return storePasswordSession(payload as PasswordSessionPayload);
}

export async function logoutPasswordAccount(sessionToken: string): Promise<void> {
  try {
    await fetch(`${clientEnv.apiBaseUrl}/auth/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  } finally {
    clearStoredAuthSession();
  }
}

async function createPasswordSession(
  action: "register" | "login",
  input: { email: string; password: string; displayName?: string }
): Promise<AuthSessionResponse> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  const payload = await readJson<PasswordSessionPayload | ApiErrorPayload>(response);
  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(
      response.status,
      "AUTH_ACCOUNT_FAILED",
      errorPayload.message ?? errorPayload.error ?? (action === "register" ? "No se pudo crear la cuenta." : "No se pudo iniciar sesión.")
    );
  }
  const result = payload as PasswordSessionPayload;
  return storePasswordSession(result);
}

function storePasswordSession(result: PasswordSessionPayload): AuthSessionResponse {
  const session: AuthSessionResponse = {
    sessionId: result.sessionId,
    sessionToken: result.token,
    tokenType: "Bearer",
    createdAt: new Date().toISOString(),
    expiresAt: new Date(result.expiresAt).toISOString(),
    user: {
      id: result.user.id,
      mode: "account",
      plan: result.user.plan ?? "free",
      ageGate: "unknown",
      provider: "password",
      email: result.user.email,
      displayName: result.user.displayName,
      emailVerified: result.user.emailVerified,
      role: result.user.role,
      mfaEnabled: result.user.mfaEnabled
    }
  };
  writeStoredSession(session);
  return session;
}

export async function requestEmailVerification(sessionToken: string): Promise<EmailDeliveryStatus> {
  return authenticatedJson<EmailDeliveryStatus>("/auth/email/verification", sessionToken, { method: "POST" });
}

export async function verifyEmailToken(token: string): Promise<void> {
  await publicJson("/auth/email/verify", { method: "POST", body: { token } });
}

export async function requestPasswordReset(email: string): Promise<EmailDeliveryStatus> {
  return publicJson<EmailDeliveryStatus>("/auth/password/forgot", { method: "POST", body: { email } });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await publicJson("/auth/password/reset", { method: "POST", body: { token, newPassword } });
  clearStoredAuthSession();
}

export async function changePassword(
  sessionToken: string,
  input: { currentPassword: string; newPassword: string; mfaCode?: string }
): Promise<void> {
  await authenticatedJson("/auth/password/change", sessionToken, { method: "POST", body: input });
}

export async function getMfaStatus(sessionToken: string): Promise<MfaStatus> {
  return authenticatedJson<MfaStatus>("/auth/mfa", sessionToken);
}

export async function beginMfaSetup(sessionToken: string): Promise<MfaSetup> {
  return authenticatedJson<MfaSetup>("/auth/mfa/setup", sessionToken, { method: "POST" });
}

export async function enableMfa(sessionToken: string, code: string): Promise<{ enabled: true; recoveryCodes: string[] }> {
  return authenticatedJson("/auth/mfa/enable", sessionToken, { method: "POST", body: { code } });
}

export async function disableMfa(sessionToken: string, password: string, code: string): Promise<void> {
  await authenticatedJson("/auth/mfa", sessionToken, { method: "DELETE", body: { password, code } });
}

export async function deleteAccount(sessionToken: string, password: string, mfaCode?: string): Promise<void> {
  await authenticatedJson("/privacy/account", sessionToken, { method: "DELETE", body: { password, mfaCode } });
  clearStoredAuthSession();
}

export async function revokeAccountSession(sessionToken: string, sessionId: string): Promise<void> {
  await authenticatedJson(`/auth/sessions/${encodeURIComponent(sessionId)}`, sessionToken, { method: "DELETE" });
}

export async function getAccountSessions(sessionToken: string): Promise<readonly AccountSession[]> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/sessions`, {
    headers: { Authorization: `Bearer ${sessionToken}` }
  });
  const payload = await readJson<{ sessions?: AccountSession[] } | ApiErrorPayload>(response);
  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(response.status, "AUTH_SESSIONS_FAILED", errorPayload.error ?? "No se pudieron leer las sesiones.");
  }
  return (payload as { sessions: AccountSession[] }).sessions;
}

export async function logoutOtherAccountSessions(sessionToken: string): Promise<number> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/logout-others`, {
    method: "POST",
    headers: { Authorization: `Bearer ${sessionToken}` }
  });
  const payload = await readJson<{ revoked?: number } | ApiErrorPayload>(response);
  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(response.status, "AUTH_LOGOUT_OTHERS_FAILED", errorPayload.error ?? "No se pudieron cerrar las otras sesiones.");
  }
  return (payload as { revoked?: number }).revoked ?? 0;
}

export async function getAuthProviders(): Promise<readonly AuthProviderStatus[]> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/providers`, {
    credentials: "include"
  });
  const payload = await readJson<ApiEnvelope<readonly AuthProviderStatus[]> | ApiErrorPayload>(
    response
  );

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(
      response.status,
      errorPayload.error ?? "AUTH_PROVIDERS_FAILED",
      errorPayload.message ?? "No se pudieron leer los proveedores de acceso."
    );
  }

  return (payload as ApiEnvelope<readonly AuthProviderStatus[]>).data;
}

export async function createAppleAuthSession(identityToken: string): Promise<AuthSessionResponse> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/auth/apple-session`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ identityToken })
  });
  const payload = await readJson<ApiEnvelope<AuthSessionResponse> | ApiErrorPayload>(response);

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AuthClientError(
      response.status,
      errorPayload.error ?? "AUTH_APPLE_FAILED",
      errorPayload.message ?? "No se pudo iniciar sesión con Apple."
    );
  }

  const session = (payload as ApiEnvelope<AuthSessionResponse>).data;
  writeStoredSession(session);
  return session;
}

export function clearStoredAuthSession(): void {
  getStorage()?.removeItem(SESSION_STORAGE_KEY);
  if (typeof window !== "undefined") window.dispatchEvent(new Event("omnisynia:session-change"));
}

export function readStoredSession(): AuthSessionResponse | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawSession = storage.getItem(SESSION_STORAGE_KEY);
    if (!rawSession) {
      return null;
    }

    const session = JSON.parse(rawSession) as Partial<AuthSessionResponse>;
    if (
      typeof session.sessionId !== "string" ||
      typeof session.sessionToken !== "string" ||
      typeof session.expiresAt !== "string" ||
      session.tokenType !== "Bearer" ||
      !session.user
    ) {
      return null;
    }

    return session as AuthSessionResponse;
  } catch {
    clearStoredAuthSession();
    return null;
  }
}

function writeStoredSession(session: AuthSessionResponse): void {
  getStorage()?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  if (typeof window !== "undefined") window.dispatchEvent(new Event("omnisynia:session-change"));
}

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

interface JsonRequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
}

async function publicJson<T = { ok: boolean }>(path: string, options: JsonRequestOptions = {}): Promise<T> {
  const response = await fetch(`${clientEnv.apiBaseUrl}${path}`, {
    method: options.method ?? "GET",
    headers: options.body === undefined ? undefined : { "Content-Type": "application/json" },
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const payload = await readJson<T | ApiErrorPayload>(response);
  if (!response.ok) throwAuthError(response, payload, "No se pudo completar la operación.");
  return payload as T;
}

async function authenticatedJson<T = { ok: boolean }>(
  path: string,
  sessionToken: string,
  options: JsonRequestOptions = {}
): Promise<T> {
  const response = await fetch(`${clientEnv.apiBaseUrl}${path}`, {
    method: options.method ?? "GET",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      ...(options.body === undefined ? {} : { "Content-Type": "application/json" })
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const payload = await readJson<T | ApiErrorPayload>(response);
  if (!response.ok) throwAuthError(response, payload, "No se pudo completar la operación.");
  return payload as T;
}

function throwAuthError(response: Response, payload: unknown, fallback: string): never {
  const errorPayload = payload as ApiErrorPayload;
  throw new AuthClientError(
    response.status,
    errorPayload.error ?? "AUTH_ACCOUNT_FAILED",
    errorPayload.message ?? errorPayload.error ?? fallback
  );
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new AuthClientError(response.status, "AUTH_INVALID_RESPONSE", "Respuesta auth no válida.");
  }
}
